import { useCallback, useEffect, useState, type ChangeEvent, type DragEvent } from 'react';
import { FilePlus, FileText, ImagePlus, Trash2, Upload, X } from 'lucide-react';
import { Button } from './button';
import { Input } from './input';
import { useFileUpload } from '../../hooks/use-file-upload';
import { cn } from '../../utils/cn';

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}

function acceptAllows(file: File, accept?: string): boolean {
  if (!accept || accept.trim() === '*' || accept.includes('*/*')) return true;
  const tokens = accept.split(',').map((t) => t.trim().toLowerCase()).filter(Boolean);
  if (!tokens.length) return true;
  const name = file.name.toLowerCase();
  const type = (file.type || '').toLowerCase();
  return tokens.some((token) => {
    if (token.endsWith('/*')) {
      const prefix = token.slice(0, -1); // e.g. "image/"
      return type.startsWith(prefix);
    }
    if (token.startsWith('.')) {
      return name.endsWith(token);
    }
    return type === token || name.endsWith(`.${token}`);
  });
}

export interface FileDropzoneProps {
  accept?: string;
  multiple?: boolean;
  disabled?: boolean;
  /** Controlled single file (e.g. FusionBankImport staging). */
  file?: File | null;
  /** Called when selection changes (single mode). */
  onFileChange?: (file: File | null) => void;
  /** Called when selection changes (multiple mode). */
  onFilesChange?: (files: File[]) => void;
  title?: string;
  description?: string;
  className?: string;
  /** Smaller height for sidebar inspectors. */
  compact?: boolean;
  /** Hide outer card chrome (title/border) — embed in an existing panel. */
  bare?: boolean;
  'data-tour'?: string;
}

/**
 * Shared evidence dropzone — PDFs, CSV/XLSX, images, UFDR, etc.
 * Vite/React (no next/image). Use everywhere officers upload documents.
 */
export function FileDropzone({
  accept,
  multiple = false,
  disabled = false,
  file: controlledFile,
  onFileChange,
  onFilesChange,
  title = 'Upload file',
  description,
  className,
  compact = false,
  bare = false,
  'data-tour': dataTour,
}: FileDropzoneProps) {
  const {
    previewUrl,
    fileName,
    file: internalFile,
    files,
    fileInputRef,
    handleThumbnailClick,
    handleFileChange,
    handleRemove,
    setSelectedFile,
  } = useFileUpload({
    multiple,
    onUpload: (f) => {
      if (multiple) return;
      onFileChange?.(f);
    },
  });

  const [isDragging, setIsDragging] = useState(false);

  // Sync controlled file (canvas drop → Bank import, parent clear, etc.)
  useEffect(() => {
    if (controlledFile === undefined) return;
    if (controlledFile === null) {
      if (internalFile) handleRemove();
      return;
    }
    if (controlledFile !== internalFile) {
      setSelectedFile(controlledFile);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only react to parent file identity
  }, [controlledFile]);

  useEffect(() => {
    if (multiple && files.length) onFilesChange?.(files);
  }, [files, multiple, onFilesChange]);

  const activeFile = controlledFile !== undefined ? controlledFile : internalFile;
  const displayName = activeFile?.name ?? fileName;
  const showPreview = !!previewUrl && !!activeFile?.type.startsWith('image/');

  const clear = useCallback(() => {
    handleRemove();
    onFileChange?.(null);
    onFilesChange?.([]);
  }, [handleRemove, onFileChange, onFilesChange]);

  const onInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      handleFileChange(e);
      if (multiple) {
        const next = Array.from(e.target.files || []);
        onFilesChange?.(next);
      } else {
        onFileChange?.(e.target.files?.[0] ?? null);
      }
    },
    [handleFileChange, multiple, onFileChange, onFilesChange],
  );

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragEnter = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) setIsDragging(true);
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      if (disabled) return;

      const dropped = Array.from(e.dataTransfer.files || []).filter((f) => acceptAllows(f, accept));
      if (!dropped.length) return;

      if (multiple) {
        const dt = new DataTransfer();
        dropped.forEach((f) => dt.items.add(f));
        const fakeEvent = {
          target: { files: dt.files },
        } as unknown as ChangeEvent<HTMLInputElement>;
        onInputChange(fakeEvent);
        return;
      }

      const first = dropped[0];
      setSelectedFile(first);
      onFileChange?.(first);
    },
    [accept, disabled, multiple, onFileChange, onInputChange, setSelectedFile],
  );

  const dropHeight = compact ? 'h-36' : 'h-52';

  const dropzone = (
    <>
      <Input
        type="file"
        accept={accept}
        multiple={multiple}
        className="hidden"
        ref={fileInputRef}
        onChange={onInputChange}
        disabled={disabled}
        data-tour={dataTour}
      />

      {!activeFile && !files.length ? (
        <div
          role="button"
          tabIndex={disabled ? -1 : 0}
          onClick={() => !disabled && handleThumbnailClick()}
          onKeyDown={(e) => {
            if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
              e.preventDefault();
              handleThumbnailClick();
            }
          }}
          onDragOver={handleDragOver}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={cn(
            'flex cursor-pointer flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed border-muted-foreground/25 bg-muted/50 transition-colors hover:bg-muted',
            dropHeight,
            isDragging && 'border-primary/50 bg-primary/5',
            disabled && 'pointer-events-none opacity-50',
          )}
        >
          <div className="rounded-full bg-background p-3 shadow-sm">
            {accept?.includes('image') && !accept.includes('.pdf') && !accept.includes('.csv') ? (
              <ImagePlus className="h-6 w-6 text-muted-foreground" />
            ) : (
              <FilePlus className="h-6 w-6 text-muted-foreground" />
            )}
          </div>
          <div className="text-center px-3">
            <p className="text-sm font-medium">Click to select</p>
            <p className="text-xs text-muted-foreground">or drag and drop here</p>
            {description && (
              <p className="mt-1 text-[10px] uppercase tracking-wide text-muted-foreground/80">
                {description}
              </p>
            )}
          </div>
        </div>
      ) : showPreview ? (
        <div className="relative">
          <div className={cn('group relative overflow-hidden rounded-lg border', dropHeight)}>
            <img
              src={previewUrl!}
              alt="Preview"
              className="absolute inset-0 h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-black/40 opacity-0 transition-opacity group-hover:opacity-100" />
            <div className="absolute inset-0 flex items-center justify-center gap-2 opacity-0 transition-opacity group-hover:opacity-100">
              <Button
                type="button"
                size="sm"
                variant="secondary"
                onClick={handleThumbnailClick}
                disabled={disabled}
                className="h-9 w-9 p-0"
              >
                <Upload className="h-4 w-4" />
              </Button>
              <Button
                type="button"
                size="sm"
                variant="destructive"
                onClick={clear}
                disabled={disabled}
                className="h-9 w-9 p-0"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
          {displayName && (
            <div className="mt-2 flex items-center gap-2 text-sm text-muted-foreground">
              <span className="truncate">{displayName}</span>
              <button
                type="button"
                onClick={clear}
                disabled={disabled}
                className="ml-auto rounded-full p-1 hover:bg-muted"
                aria-label="Remove file"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>
      ) : (
        <div
          className={cn(
            'flex items-center gap-3 rounded-lg border border-border bg-muted/40 p-3',
            disabled && 'opacity-50',
          )}
        >
          <FileText className="h-8 w-8 shrink-0 text-primary" />
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-foreground">
              {multiple && files.length > 1
                ? `${files.length} files selected`
                : displayName}
            </p>
            {activeFile && !multiple && (
              <p className="text-xs text-muted-foreground">{formatBytes(activeFile.size)}</p>
            )}
            {multiple && files.length > 0 && (
              <p className="text-xs text-muted-foreground truncate">
                {files.map((f) => f.name).join(', ')}
              </p>
            )}
          </div>
          <div className="flex shrink-0 gap-1">
            <Button
              type="button"
              size="sm"
              variant="secondary"
              onClick={handleThumbnailClick}
              disabled={disabled}
              className="h-8 w-8 p-0"
              aria-label="Replace file"
            >
              <Upload className="h-3.5 w-3.5" />
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={clear}
              disabled={disabled}
              className="h-8 w-8 p-0"
              aria-label="Remove file"
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      )}
    </>
  );

  if (bare) {
    return <div className={cn('w-full space-y-2', className)}>{dropzone}</div>;
  }

  return (
    <div
      className={cn(
        'w-full space-y-3 rounded-xl border border-border bg-card p-4 shadow-sm',
        compact && 'p-3',
        className,
      )}
    >
      {(title || description) && (
        <div className="space-y-1">
          {title && <h3 className="text-sm font-medium">{title}</h3>}
          {description && !activeFile && (
            <p className="text-xs text-muted-foreground">{description}</p>
          )}
        </div>
      )}
      {dropzone}
    </div>
  );
}
